import { useMemo } from "react";
import { Activity, Clock3, Receipt, Zap } from "lucide-react";
import type { UseQueryResult } from "@tanstack/react-query";
import { formatMoney } from "@empite/api-client";
import type { Money, UsagePage, UsageRecord, UsageSummary } from "@empite/api-client";
import { useCustomerApplications } from "../applications";
import type { PortalApplication } from "../applications";
import { RECENT_LIMIT, useUsageAcross } from "../billing";
import { AppBadge } from "../ui/app-badge";
import type { AppIdentity } from "../ui/app-identity";
import { Button } from "../ui/button";
import { Card, CardBody, CardFooter, CardHeader } from "../ui/card";
import { DataTable } from "../ui/data-table";
import type { Column } from "../ui/data-table";
import { EmptyState } from "../ui/empty-state";
import { formatCount, formatTimestamp, relativeTime } from "../ui/format";
import { Notice } from "../ui/notice";
import { PageHeader } from "../ui/page-header";
import { UiLink } from "../ui/router";
import { Skeleton } from "../ui/skeleton";
import { StatCard } from "../ui/stat-card";
import { StatusPill } from "../ui/status-pill";
import { UsageChart } from "../ui/usage-chart";
import {
  CHART_DAYS,
  appIdentity,
  chartPoints,
  chartSeries,
  formatTotals,
  reservationPillTone,
  sumByCurrency,
} from "./billing-common";
import type { ChartInput } from "./billing-common";
import { errorMessage, RequireSession } from "./guards";

/** One request, carrying the application it belongs to. The API answers per
 *  application, so the name has to be attached here or the merged table has a
 *  column it cannot fill. */
interface Activity {
  id: string;
  app: AppIdentity;
  record: UsageRecord;
}

/**
 * Usage — what every application spent, on one page.
 *
 * This is the only screen in the portal that reads across wallet boundaries,
 * and it is allowed to because it only READS. Every figure on it is summed in
 * the browser from per-application answers; nothing here can move credit, and
 * no request it makes is scoped to more than one application.
 */
export function Usage() {
  return <RequireSession>{() => <UsageDashboard />}</RequireSession>;
}

function UsageDashboard() {
  const { applications, isPending, error } = useCustomerApplications();
  const usage = useUsageAcross(applications);

  const loading = isPending || usage.some((query) => query.isPending);
  const failure = error ?? usage.find((query) => query.error)?.error;

  const rows = useMemo(() => perApplication(applications, usage), [applications, usage]);
  const activity = useMemo(() => recentActivity(rows), [rows]);

  const charts = useMemo<ChartInput[]>(
    () => rows.map((row) => ({ application: row.application, records: row.records })),
    [rows],
  );
  const series = useMemo(() => chartSeries(charts), [charts]);
  const points = useMemo(() => chartPoints(charts), [charts]);

  const tokens = useMemo(() => sumTokens(rows), [rows]);

  const settled = sumByCurrency(rows.map((row) => row.summary?.settled_total));
  const held = sumByCurrency(rows.map((row) => row.summary?.currently_held));
  const requests = rows.reduce((total, row) => total + (row.summary?.requests ?? 0), 0);
  const billed = rows.filter((row) => (row.summary?.requests ?? 0) > 0).length;
  const last = latestRequest(rows);

  return (
    <>
      <PageHeader
        title="Usage"
        description="Every request the suite priced against your wallets, across all your applications."
        actions={
          <Button href="/wallets" variant="secondary">
            Your wallets
          </Button>
        }
      />

      {failure ? (
        <Notice tone="danger" title="Some usage could not be loaded" className="mb-6">
          {errorMessage(failure)}
        </Notice>
      ) : null}

      {!isPending && applications.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Activity className="size-6" aria-hidden />}
            title="Nothing has been spent yet"
            body="Usage appears here once an application on one of your devices makes its first request."
            hint="Credit for that comes from the application's own wallet."
            action={<Button href="/wallets">Your wallets</Button>}
          />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              accent
              label="Requests"
              value={loading ? <Skeleton className="h-7 w-20" /> : formatCount(requests)}
              footnote={`priced across ${formatCount(billed)} ${
                billed === 1 ? "application" : "applications"
              }`}
              icon={<Zap className="size-4" aria-hidden />}
            />
            <StatCard
              label="Charged"
              value={loading ? <Skeleton className="h-7 w-24" /> : formatTotals(settled)}
              footnote="settled against your wallets"
              icon={<Receipt className="size-4" aria-hidden />}
            />
            <StatCard
              label="Held for requests in flight"
              value={loading ? <Skeleton className="h-7 w-24" /> : formatTotals(held)}
              footnote={held.length === 0 ? "nothing is currently held" : "released on completion"}
              icon={<Clock3 className="size-4" aria-hidden />}
            />
            <StatCard
              label="Last request"
              value={
                loading ? (
                  <Skeleton className="h-7 w-28" />
                ) : last ? (
                  <span className="text-lg">{relativeTime(last)}</span>
                ) : (
                  "—"
                )
              }
              footnote={last ? formatTimestamp(last) : "no request yet"}
              icon={<Activity className="size-4" aria-hidden />}
            />
          </div>

          <Card className="mt-5">
            <CardHeader
              title="Tokens"
              action={
                <span className="text-xs text-ink-4">across every application</span>
              }
            />
            <CardBody>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <StatCard
                  accent
                  label="Total tokens"
                  value={loading ? <Skeleton className="h-7 w-24" /> : formatCount(tokens.total)}
                  footnote="all five kinds added together, counted equally"
                  icon={<Zap className="size-4" aria-hidden />}
                />
                {/* Every dimension is drawn, including the ones sitting at
                    zero. A provider that does not report cache writes should
                    make the screen say "0", not make the row disappear —
                    otherwise the day a provider starts reporting it looks like
                    new spending rather than a newly visible number. */}
                {TOKEN_DIMENSIONS.map((dimension) => (
                  <StatCard
                    key={dimension.key}
                    label={dimension.label}
                    value={
                      loading ? (
                        <Skeleton className="h-7 w-20" />
                      ) : (
                        formatCount(tokens[dimension.key])
                      )
                    }
                    footnote={dimension.hint}
                  />
                ))}
              </div>
            </CardBody>
            <CardFooter>
              <p className="text-xs text-ink-3">
                Counted from every attempt the suite made on your behalf, including one that
                failed over to another provider — a provider that billed for tokens before
                falling over still billed for them. The total is the plain sum of the five: no
                kind of token counts for more than another here, whatever it costs.
              </p>
            </CardFooter>
          </Card>

          <Card className="mt-5">
            <CardHeader title={`Requests — last ${CHART_DAYS} days`} />
            <CardBody>
              {activity.length > 0 ? (
                <UsageChart series={series} points={points} />
              ) : (
                <EmptyState
                  size="sm"
                  title={loading ? "Adding up your usage" : "No requests yet"}
                  body={
                    loading
                      ? undefined
                      : "The chart fills in once an application makes its first priced request."
                  }
                />
              )}
            </CardBody>
            <CardFooter>
              <p className="text-xs text-ink-3">
                Counted from the most recent {RECENT_LIMIT} requests per application, so this is
                recent activity rather than a complete tally — the totals above come from the
                suite&rsquo;s own summary.
              </p>
            </CardFooter>
          </Card>

          <Card className="mt-5 overflow-hidden">
            <CardHeader title="By application" />
            <DataTable
              columns={applicationColumns}
              rows={rows}
              caption="Requests, tokens, charges and holds per application"
              empty={
                <EmptyState
                  size="sm"
                  title={loading ? "Adding up your usage" : "No application has spent anything yet"}
                />
              }
            />
          </Card>

          <Card className="mt-5 overflow-hidden">
            <CardHeader
              title="Recent requests"
              action={
                <span className="text-xs text-ink-4">up to {RECENT_LIMIT} per application</span>
              }
            />
            <DataTable
              columns={activityColumns}
              rows={activity}
              caption="The most recent priced requests across every application"
              empty={
                <EmptyState
                  size="sm"
                  title={loading ? "Adding up your usage" : "No requests yet"}
                  body={
                    loading
                      ? undefined
                      : "Each request appears here as soon as the suite prices it."
                  }
                />
              }
            />
          </Card>

          <Notice
            tone="info"
            title="Credits belong to one application"
            className="mt-5"
          >
            Each application has its own wallet, and each wallet is spent only on that
            application. Balances are never transferred between applications and are not
            refundable to a card as credit.
          </Notice>
        </>
      )}
    </>
  );
}

/** The five token dimensions the suite prices, by the name the API answers
 *  with. */
type TokenDimension =
  | "input_tokens"
  | "cached_input_tokens"
  | "cache_write_tokens"
  | "output_tokens"
  | "reasoning_tokens";

/** The dimensions in the FIXED order the pricing engine declares them
 *  (internal/billing/pricing.go:Usage). Keeping the screen's order the same as
 *  the engine's means a reader comparing this panel against an invoice, the
 *  admin analytics or a price list reads the same five in the same sequence. */
const TOKEN_DIMENSIONS: ReadonlyArray<{ key: TokenDimension; label: string; hint: string }> = [
  { key: "input_tokens", label: "Input", hint: "prompt text sent to the model" },
  { key: "cached_input_tokens", label: "Cached input", hint: "prompt served from the provider's cache" },
  { key: "cache_write_tokens", label: "Cache write", hint: "prompt written into the provider's cache" },
  { key: "output_tokens", label: "Output", hint: "text the model wrote back" },
  { key: "reasoning_tokens", label: "Reasoning", hint: "thinking the model charged for but did not return" },
];

type TokenTotals = Record<TokenDimension, number> & { total: number };

/** Adds the dimensions up across applications. An application with no summary
 *  yet contributes nothing rather than blanking the panel, because the page
 *  renders while its slowest wallet is still answering.
 *
 *  `total` is taken from the API's own `total_tokens` rather than re-added
 *  here: the total a customer sees and the total an operator sees have to be
 *  the same arithmetic, and that arithmetic lives on the server. */
function sumTokens(rows: ApplicationUsage[]): TokenTotals {
  const totals: TokenTotals = {
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total: 0,
  };
  for (const row of rows) {
    const summary = row.summary;
    if (!summary) {
      continue;
    }
    for (const dimension of TOKEN_DIMENSIONS) {
      totals[dimension.key] += summary[dimension.key] ?? 0;
    }
    totals.total += summary.total_tokens ?? 0;
  }
  return totals;
}

interface ApplicationUsage {
  id: string;
  application: PortalApplication;
  app: AppIdentity;
  summary: UsageSummary | undefined;
  records: UsageRecord[];
}

function perApplication(
  applications: PortalApplication[],
  queries: Array<UseQueryResult<UsagePage | null>>,
): ApplicationUsage[] {
  return applications.map((application, index) => {
    const page = queries[index]?.data ?? null;
    return {
      id: application.id,
      application,
      app: appIdentity(application, index),
      summary: page?.summary,
      records: page?.usage ?? [],
    };
  });
}

/** The merged feed, newest first. Each application answered with its own most
 *  recent page, so the merge is a sort — not a second request. */
function recentActivity(rows: ApplicationUsage[]): Activity[] {
  return rows
    .flatMap((row) => row.records.map((record) => ({ id: record.id, app: row.app, record })))
    .sort((a, b) => b.record.created_at.localeCompare(a.record.created_at));
}

/** The newest `last_request_at` any application reported. Read from `summary`
 *  rather than the record list, which is capped and so could be older. */
function latestRequest(rows: ApplicationUsage[]): string | undefined {
  return rows
    .map((row) => row.summary?.last_request_at)
    .filter((at): at is string => Boolean(at))
    .sort((a, b) => b.localeCompare(a))[0];
}

/** What a request cost. A settled request has a final price; one still in
 *  flight has only the amount held against it, and saying which is which is
 *  the difference between a bill and an estimate. */
function cost(record: UsageRecord): { amount: Money; final: boolean } {
  return record.settled
    ? { amount: record.settled, final: true }
    : { amount: record.reserved, final: false };
}

/** An amount as the design draws money in a table: monospaced digits, right
 *  aligned by the column. */
function MoneyCell({ amount }: { amount: Money | undefined }) {
  if (!amount) {
    return <span className="text-ink-4">—</span>;
  }
  return <span className="tnum font-semibold text-ink">{formatMoney(amount)}</span>;
}

const applicationColumns: Array<Column<ApplicationUsage>> = [
  {
    key: "application",
    header: "Application",
    cell: (row) => (
      <UiLink href={`/wallets/${row.application.id}`} className="block hover:opacity-80">
        <AppBadge app={row.app} showSlug />
      </UiLink>
    ),
  },
  {
    key: "requests",
    header: "Requests",
    align: "right",
    cell: (row) => (
      <span className="tnum text-ink-2">{formatCount(row.summary?.requests ?? 0)}</span>
    ),
  },
  {
    // The application's own total, the same unweighted sum of five the panel
    // above reports — so a reader can check the panel by adding this column up.
    key: "tokens",
    header: "Tokens",
    align: "right",
    cell: (row) => (
      <span className="tnum text-ink-2">{formatCount(row.summary?.total_tokens ?? 0)}</span>
    ),
  },
  {
    key: "charged",
    header: "Charged",
    align: "right",
    cell: (row) => <MoneyCell amount={row.summary?.settled_total} />,
  },
  {
    key: "held",
    header: "Held",
    align: "right",
    cell: (row) => <MoneyCell amount={row.summary?.currently_held} />,
  },
  {
    key: "last",
    header: "Last request",
    align: "right",
    cell: (row) =>
      row.summary?.last_request_at ? (
        <span className="text-ink-3">{relativeTime(row.summary.last_request_at)}</span>
      ) : (
        <span className="text-ink-4">—</span>
      ),
  },
];

const activityColumns: Array<Column<Activity>> = [
  {
    key: "when",
    header: "When",
    cell: (row) => (
      <span className="tnum whitespace-nowrap text-ink-3">
        {formatTimestamp(row.record.created_at)}
      </span>
    ),
  },
  {
    key: "application",
    header: "Application",
    cell: (row) => <AppBadge app={row.app} />,
  },
  {
    key: "model",
    header: "Model",
    cell: (row) => (
      <span className="font-mono text-2xs text-ink-2">
        {row.record.model ?? row.record.provider ?? "—"}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => (
      <StatusPill tone={reservationPillTone(row.record.status)} dot>
        {row.record.status}
      </StatusPill>
    ),
  },
  {
    key: "cost",
    header: "Cost",
    align: "right",
    cell: (row) => {
      const { amount, final } = cost(row.record);
      return (
        <span className="whitespace-nowrap">
          <MoneyCell amount={amount} />
          {final ? null : <span className="ml-1.5 text-2xs text-ink-4">held</span>}
        </span>
      );
    },
  },
];
